'use client';

import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { formatPrice } from 'utils';
import { ColumnChartProps } from 'types/common';

const formatYAxis = (value: number, suffix?: string) => {
  const absValue = Math.abs(value);
  if (absValue >= 10000000) {
    return `${Math.floor(absValue / 10000000)}M`;
  } else if (absValue >= 1000) {
    return `${Math.floor(absValue / 1000)}K`;
  }
  return absValue.toString() + (suffix || '');
};

const ColumnChart: React.FC<ColumnChartProps> = ({
  title,
  data,
  className = '',
  onExpandClick,
  yAxisSuffix = '',
  // type = 'orders',
}) => {
  const transformedData = useMemo(() => {
    if (!data) return [];

    return data.map((item) => ({
      ...item,
      purchaseOrders: -Math.abs(item.purchaseOrders || 0),
    }));
  }, [data]);

  return (
    <div
      className={`bg-white rounded-xl shadow-[1px_1px_10.100000381469727px_0px_rgba(0,0,0,0.15)] outline outline-1 outline-offset-[-1px] outline-black/10 shadow-[4px_4px_12px_0px_rgba(9,9,11,0.04)] ${className}`}
    >
      {/* Header */}
      <div className='p-6 flex justify-between items-center gap-2.5'>
        <h3 className="text-Text-Primary text-base font-bold font-['Inter'] leading-snug">{title}</h3>
        <ArrowSquareOut
          size={16}
          className='text-[var(--textBlack100)] cursor-pointer hover:opacity-70 transition-opacity'
          onClick={onExpandClick}
        />
      </div>

      {/* Chart Content */}
      <div className='lg:px-6 px-0 pt-2 h-[360px]'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart
            data={transformedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
            barCategoryGap='20%'
            maxBarSize={40}
          >
            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e5e7eb' />
            <XAxis
              dataKey='date'
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: 'Roboto', fill: 'var(--textBlack56)' }}
              height={50}
            />
            <YAxis
              tickFormatter={(value) => formatYAxis(value, yAxisSuffix)}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: 'Roboto', fill: 'var(--textBlack56)' }}
              width={60}
            />
            <ReferenceLine y={0} stroke='#e5e7eb' />
            <Tooltip
              formatter={(value: number, name: string) => {
                const absValue = Math.abs(value);
                if (name === 'saleOrders') return [formatPrice(absValue), 'Thu (Bán hàng)'];
                if (name === 'purchaseOrders') return [formatPrice(absValue), 'Chi (Nhập hàng)'];
                return [formatPrice(absValue), name];
              }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
              cursor={{ fill: 'var(--textBlack4)' }}
            />
            <Bar
              dataKey='saleOrders'
              // stackId='a'
              fill='var(--keyColor2)'
              radius={[4, 4, 0, 0]}
              name='Thu (Bán hàng)'
            />
            <Bar
              dataKey='purchaseOrders'
              // stackId='a'
              fill='var(--keyColor3)'
              radius={[4, 4, 0, 0]}
              name='Chi (Nhập hàng)'
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className='px-6 pt-0 pb-6 flex justify-center items-start gap-6'>
        <div className='flex items-center gap-2'>
          <div className='w-3 h-3 rounded-sm bg-[var(--keyColor2)]'></div>
          <span className="text-gray-600 text-xs font-normal font-['Inter']">Thu (Bán hàng)</span>
        </div>
        <div className='flex items-center gap-2'>
          <div className='w-3 h-3 rounded-sm bg-[var(--keyColor3)]'></div>
          <span className="text-gray-600 text-xs font-normal font-['Inter']">Chi (Nhập hàng)</span>
        </div>
      </div>
    </div>
  );
};

export default ColumnChart;
